import { APP_VERSION } from './index.js'

/**
 * Parses a semantic version string (e.g. '1.2.3') into an array of numbers.
 * @param {string} version
 * @returns {[number, number, number]} [major, minor, patch]
 */
const parse = (version) => version.split('.').map(Number)

/**
 * Compares two semantic versions.
 * @param {string} a - First version string (e.g. '1.2.3')
 * @param {string} b - Second version string (e.g. '1.3.0')
 * @returns {number} - Returns 1 if a > b, -1 if a < b, 0 if equal
 */
const compareVersions = (a, b) => {
	const [aMajor, aMinor, aPatch] = parse(a)
	const [bMajor, bMinor, bPatch] = parse(b)

	if (aMajor !== bMajor) return aMajor - bMajor
	if (aMinor !== bMinor) return aMinor - bMinor
	return aPatch - bPatch
}

/**
 * Checks if the current app version is at least the specified version.
 *
 * Special case: if the version is '0.0.0', it is treated as invalid and returns false.
 *
 * @param {string} version - Target version in `major.minor.patch` format.
 * @returns {boolean} - Returns true if current version >= target version, false otherwise.
 *
 * @example
 * requiredVersion('1.0.0') // true if APP_VERSION is 1.0.0 or higher
 * requiredVersion('0.0.0') // false (invalid version marker)
 */
const requiredVersion = (version) => {
	if (!version) return true

	const [rMajor, rMinor, rPatch] = parse(version)
	if (rMajor === 0 && rMinor === 0 && rPatch === 0) return false

	const [cMajor, cMinor, cPatch] = parse(APP_VERSION)

	if (cMajor > rMajor) return true
	if (cMajor < rMajor) return false

	if (cMinor > rMinor) return true
	if (cMinor < rMinor) return false

	if (cPatch > rPatch) return true
	if (cPatch < rPatch) return false

	return true // Versions are exactly equal
}

/**
 * Checks if the current app version is less than the specified version.
 *
 * @param {string} version - Target version in `major.minor.patch` format.
 * @returns {boolean} - Returns true if current version < target version, false otherwise.
 *
 * @example
 * showUntilVersion('2.0.0') // true if APP_VERSION is less than 2.0.0
 */
const showUntilVersion = (version) => compareVersions(APP_VERSION, version) < 0

export { requiredVersion, showUntilVersion }
